import {useRef, useEffect, useState} from 'react';
import type {
  ComponentPropsWithoutRef,
  ForwardRefExoticComponent,
  MouseEvent,
  ReactNode,
  RefAttributes,
  UIEvent
} from 'react';
import {useRouter} from 'next/router';
import Link from '@/links';
import dynamic from 'next/dynamic';
import cx from 'classnames';
import {useTranslations} from 'next-intl';
import {List} from '@/ui/lists/list-icons';
import debounced from 'utils/debounced';
import AdBanner from '@/ui/ad-banner';
import AdBannerCopy from '@/ui/ad-banner/ad-banner-copy';
import {withHiddenDesktop1100, withVisibleDesktop1100} from '@/hocs/withResponsiveUtils';
import useOnClickOutside from '@/hooks/useClickOutside';
import {extractLocaleFromPath} from 'helpers/i18n';
import UserHeader from './user-header';
import NavDropdown from './nav-dropdown';
import type {
  HeaderProps,
  MobileMenuHamburgerCTAProps,
  NavItem,
  NavLinkNavItem
} from '../../types/components/header';
import styles from './header.module.scss';
import LogoDropdown from '../logo-dropdown';

const RESOURCES_NAV_CHILDREN: ReadonlyArray<{id: string; href: string; target?: string}> = [
  {id: 'ResourceLibrary', href: 'https://resources.workable.com', target: '_blank'},
  {id: 'HelpCenter', href: 'https://help.workable.com', target: '_blank'},
  {id: 'SearchForJobs', href: 'https://jobs.workable.com', target: '_blank'},
  {id: 'PartnersIntegrations', href: 'https://partners.workable.com', target: '_blank'}
];

const INDUSTRIES_NAV_CHILDREN: ReadonlyArray<{id: string; href: string}> = [
  {id: 'ConstructionEngineering', href: '/industry-construction-and-engineering'},
  {id: 'HealthcareServices', href: '/industry-healthcare'},
  {id: 'RealEstatePropertyServices', href: '/industry-real-estate-and-property-management'},
  {id: 'Technology', href: '/industry-technology'},
  {id: 'ProfessionalServices', href: '/industry-professional-services'}
];

const LogoPrimary = dynamic(import('@/svg/logos/logoPrimary'), {loading: () => null});
type ResponsiveDivProps = ComponentPropsWithoutRef<'div'>;
const ResponsiveDiv = (props: ResponsiveDivProps) => <div {...props} />;
const VisibleOnlyOnMobile = withHiddenDesktop1100(ResponsiveDiv);
const VisibleOnlyOnDesktop = withVisibleDesktop1100(ResponsiveDiv);
const TypedAdBanner = AdBanner as ForwardRefExoticComponent<
  {children: ReactNode; onClose?: () => void} & RefAttributes<HTMLDivElement>
>;

const MobileMenuHamburgerCTA = ({onClick, className}: MobileMenuHamburgerCTAProps) => (
  <button
    type="button"
    data-testid="mobile-menu-button"
    data-ui="mobile-burger-menu-button"
    className={className}
    onClick={onClick}
    name="burger-menu-button"
    aria-label="menu button">
    <span className={styles['header-hamburger-inner']} />
  </button>
);

const Header = ({lightHeader = false, showAdBanner = true, onBannerDismiss}: HeaderProps) => {
  const router = useRouter();
  const adBannerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [sticky, setSticky] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileMenuScrolled, setIsMobileMenuScrolled] = useState(false);
  const [isNeutral50Header, setIsNeutral50Header] = useState(!lightHeader);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [openNavDropdownId, setOpenNavDropdownId] = useState<string | null>(null);

  const currentPath =
    extractLocaleFromPath((router.asPath || '/').split(/[?#]/)[0]).path.replace(/\/$/, '') || '/';

  const isNavLinkCurrent = (href: string) => {
    const normalizedHref = href.replace(/\/$/, '') || '/';
    return currentPath === normalizedHref || currentPath.startsWith(`${normalizedHref}/`);
  };

  const closeUserDropdown = () => setUserDropdownOpen(false);

  const toggleUserDropdown = () => {
    setUserDropdownOpen(prev => !prev);
  };

  useEffect(() => {
    if (!mobileMenuOpen) {
      setOpenNavDropdownId(null);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onWindowScroll = () => {
      const top = adBannerRef.current ? adBannerRef.current.offsetHeight : 0;

      setSticky(window.scrollY > top);
      if (!lightHeader) return;
      setIsNeutral50Header(window.scrollY > top);
    };

    const onWindowScrollDebounced = debounced(10, onWindowScroll);

    window.addEventListener('scroll', onWindowScrollDebounced, {passive: true});

    return () => {
      window.removeEventListener('scroll', onWindowScrollDebounced);
    };
  }, []);

  const toggleMobileMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    document.querySelector('html')?.classList.toggle('non-scrollable');
    setMobileMenuOpen(curr => !curr);
  };

  const closeMenus = () => {
    document.querySelector('html')?.classList.remove('non-scrollable');
    setMobileMenuOpen(false);
    setUserDropdownOpen(false);
    setOpenNavDropdownId(null);
  };

  useOnClickOutside(menuRef, () => closeMenus());

  const getNavLink = (item: NavLinkNavItem) => {
    const isCurrent = isNavLinkCurrent(item.href);

    return (
      <Link
        variant="cta-default-no-arrow"
        href={item.href}
        onClick={closeMenus}
        className={cx(styles['nav-link'], isCurrent && styles['nav-link--current'])}
        customAttributes={{
          'data-testid': `nav-link-${item.id}`,
          ...(isCurrent ? {'aria-current': 'page'} : {})
        }}>
        {item.text}
      </Link>
    );
  };

  const toggleNavDropdown = (id: string) => {
    setOpenNavDropdownId(prev => (prev === id ? null : id));
  };

  const renderNavItem = (item: NavItem) => {
    if (item.type === 'link') {
      return getNavLink(item);
    }

    const isCurrent = item.children.some(child => isNavLinkCurrent(child.href));

    return (
      <NavDropdown
        id={item.id}
        label={item.label}
        items={item.children}
        triggerClassName={cx(styles['nav-link'], isCurrent && styles['nav-link--current'])}
        isOpen={openNavDropdownId === item.id}
        isItemCurrent={isNavLinkCurrent}
        closeOnOutsideClick={!mobileMenuOpen}
        onToggle={() => toggleNavDropdown(item.id)}
        onNavigate={closeMenus}
      />
    );
  };

  const setMobileMenuScrolledDebounced = debounced(10, (scrollTop: number) => {
    setIsMobileMenuScrolled(scrollTop >= 5);
  });

  const onHeaderMobileScroll = (event: UIEvent<HTMLDivElement>) => {
    setMobileMenuScrolledDebounced(event.currentTarget.scrollTop);
  };

  const t = useTranslations('shared.header.nav');
  const resourcesT = useTranslations('shared.header.nav.resources');
  const industriesT = useTranslations('shared.header.nav.industries');

  const navItems: NavItem[] = [
    {
      type: 'link',
      id: 'product',
      text: t('product'),
      href: '/features'
    },
    {
      type: 'link',
      id: 'ai',
      text: t('ai'),
      href: '/workable-ai'
    },
    {
      type: 'dropdown',
      id: 'industries',
      label: industriesT('label'),
      children: INDUSTRIES_NAV_CHILDREN.map(({id, href}) => ({
        id,
        text: industriesT(id),
        href
      }))
    },
    {
      type: 'link',
      id: 'customers',
      text: t('customers'),
      href: '/customers'
    },
    {
      type: 'dropdown',
      id: 'resources',
      label: resourcesT('label'),
      children: RESOURCES_NAV_CHILDREN.map(({id, href, target}) => ({
        id,
        text: resourcesT(id),
        href,
        target
      }))
    },
    {
      type: 'link',
      id: 'pricing',
      text: t('pricing'),
      href: '/pricing'
    }
  ];

  const adBanner =
    (showAdBanner && (
      <TypedAdBanner ref={adBannerRef} onClose={onBannerDismiss}>
        <AdBannerCopy />
      </TypedAdBanner>
    )) ||
    null;

  const mainNavigationLinks = navItems.map(item => (
    <li key={`nav_${item.id}`}>{renderNavItem(item)}</li>
  ));

  const appHeader = (
    <header
      data-testid="main-header"
      className={cx('header', styles.header, {
        [styles['header-light']]: lightHeader,
        [styles['header-sticky']]: sticky,
        [styles['header-mobile-menu-open']]: mobileMenuOpen,
        [styles['header-has-ad-banner']]: adBanner !== null,
        [styles['header-fixed']]: showAdBanner
      })}>
      <nav>
        <div
          className={cx({
            [styles['header-nav-neutral50']]: isNeutral50Header
          })}>
          <VisibleOnlyOnMobile>
            <Link href="/" title="Workable logo" className={styles['header-logo']}>
              <LogoPrimary lightHeader={!isNeutral50Header} />
            </Link>
          </VisibleOnlyOnMobile>

          <MobileMenuHamburgerCTA
            onClick={toggleMobileMenu}
            className={cx(styles['header-hamburger'])}
          />

          <div
            ref={menuRef}
            className={cx(styles['header-inner-wrapper'], {
              [styles['header-inner-scrolled']]: isMobileMenuScrolled
            })}>
            <div className={cx(styles['header-inner'])} onScroll={onHeaderMobileScroll}>
              <div className={styles['header-left']}>
                <VisibleOnlyOnDesktop>
                  <LogoDropdown lightHeader={!isNeutral50Header} />
                </VisibleOnlyOnDesktop>

                <List className={styles['nav-list']}>{mainNavigationLinks}</List>
              </div>
              <div className={cx(styles['header-right'])}>
                <UserHeader
                  userDropdownOpen={userDropdownOpen}
                  isNeutral50Header={isNeutral50Header}
                  toggleUserDropdown={toggleUserDropdown}
                  closeUserDropdown={closeUserDropdown}
                  closeMenus={closeMenus}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );

  return (
    <>
      {adBanner}
      {appHeader}
    </>
  );
};

export default Header;
